import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Observable, combineLatest, distinctUntilChanged, filter, map, shareReplay, startWith, take } from 'rxjs';
import { SiteLanguage } from '../dto/response/site.settings';
import { SiteSettingState } from '../store/states/siteSetting.state';

/**
 * Idioma de CONTENIDO de la storefront: es el mismo idioma de UI de @ngx-translate
 * (translate.currentLang), que el interceptor HTTP envía como Accept-Language al backend.
 *
 * Los idiomas soportados YA NO están hardcodeados aquí: vienen del site (settings/v2 →
 * language), a través de SiteSettingState.supportedLanguages / defaultLanguage.
 *
 * Idiomas de contenido sin JSON de UI propio (p.ej. zh, de — assets/i18n/ solo tiene
 * en/es/fr/ar): translate.use(iso) intentará cargar `assets/i18n/{iso}.json`, que no
 * existe (404). ngx-translate no expone un "use silencioso": si la carga falla, no llama
 * a su changeLang() interno y currentLang/onLangChange se quedan sin actualizar. Por eso
 * el switchLanguage() de abajo replica ese changeLang a mano en el error handler — el
 * contenido del backend siempre se traduce (currentLang correcto → Accept-Language
 * correcto); el chrome de UI cae al defaultLang ya cargado (fallback nativo de
 * ngx-translate al resolver claves ausentes). No es un error, es el comportamiento
 * aceptado por diseño.
 */
@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private static readonly STORAGE_KEY = 'naaora_content_lang';

  // Idiomas elegibles en el selector = default ∪ supported (ver SiteSettingState.availableLanguages).
  readonly availableLanguages$: Observable<SiteLanguage[]> = this.store.select(
    SiteSettingState.availableLanguages,
  );

  // onLangChange es un EventEmitter (hot, sin replay): un suscriptor TARDÍO (p.ej. el combo del
  // header, que se crea después del arranque) se perdería la emisión del idioma ya resuelto y se
  // quedaría con el startWith viejo → el combo mostraba un idioma distinto al real. shareReplay(1)
  // cachea el último idioma para que cualquier suscriptor, entre cuando entre, reciba el vigente.
  readonly currentLanguage$: Observable<string> = this.translate.onLangChange.pipe(
    map((event) => event.lang),
    startWith(this.translate.currentLang || this.translate.defaultLang),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  /**
   * Dirección del documento ('rtl' | 'ltr') como FUNCIÓN DETERMINISTA de (idioma activo ×
   * idiomas del site). Reactiva: se recalcula tanto al cambiar de idioma como cuando llegan
   * los settings. Esto elimina la carrera del arranque (antes applyRtl leía el store vacío en
   * el constructor y dejaba una dirección incorrecta que no se corregía). Nunca queda RTL
   * "pegado": si el idioma resuelto no es RTL, la dirección vuelve a 'ltr' sola.
   */
  readonly direction$: Observable<'rtl' | 'ltr'> = combineLatest([
    this.currentLanguage$,
    this.store.select(SiteSettingState.availableLanguages),
  ]).pipe(
    map(([iso, langs]) => (langs.find((l) => l.isoCode === iso)?.rtl ? 'rtl' : 'ltr')),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  constructor(
    private translate: TranslateService,
    private store: Store,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
  ) {
    // ÚNICO switch de idioma automático al arrancar. Antes del render, el APP_INITIALIZER
    // (app.config.ts) ya cargó un idioma provisional de UI (bloqueante). Aquí, cuando llegan
    // los idiomas reales del site, se resuelve el idioma de contenido DEFINITIVO (persistido →
    // navegador → default del site, validado contra `supported`) con un solo switchLanguage().
    // take(1): solo la primera llegada — un cambio manual del usuario nunca debe ser pisado por
    // un refresco posterior de settings. Un único switch aquí evita la carrera anterior (dos
    // translate.use() async en paralelo dejaban el `dir` descolgado del idioma real).
    combineLatest([
      this.store.select(SiteSettingState.supportedLanguages),
      this.store.select(SiteSettingState.defaultLanguage),
    ])
      .pipe(
        filter(([supported, defaultLanguage]) => supported.length > 0 && !!defaultLanguage),
        take(1),
      )
      .subscribe(([supported, defaultLanguage]) => {
        this.resolveInitialLanguage(supported, defaultLanguage!);
      });

    // Dirección (dir) + atributo lang del <html> derivados reactivamente. Única fuente que
    // toca `dir`; siempre coherente con el idioma activo y los settings, sin carreras.
    if (isPlatformBrowser(this.platformId)) {
      combineLatest([this.currentLanguage$, this.direction$]).subscribe(([iso, direction]) => {
        const html = this.document.documentElement;
        html.setAttribute('dir', direction);
        if (iso) {
          html.setAttribute('lang', iso);
        }
      });
    }
  }

  /**
   * Cambia el idioma de contenido activo. Público: lo usa tanto la resolución
   * automática (inicial / persistida) como el selector manual del usuario (app-language).
   */
  switchLanguage(iso: string): void {
    this.translate.use(iso).subscribe({
      next: () => this.onLanguageApplied(iso),
      error: () => {
        // Sin JSON de UI para este idioma de contenido: forzamos currentLang a mano
        // (ver cabecera de la clase) para que Accept-Language viaje correcto igualmente.
        this.translate.currentLang = iso;
        this.translate.onLangChange.emit({
          lang: iso,
          translations: this.translate.translations[this.translate.defaultLang],
        });
        this.onLanguageApplied(iso);
      },
    });
  }

  getCurrentLanguage(): string {
    return this.translate.currentLang || this.translate.defaultLang;
  }

  /**
   * Resuelve un objeto i18n aplanado del backend ({ default_value, en, es, ... }) para
   * el idioma de cliente activo, cayendo a default_value.
   * Usado para etiquetas backend-driven y agnósticas al gateway (p.ej. métodos de pago).
   */
  resolveI18n(label?: { default_value: string; [lang: string]: string }): string {
    if (!label) return '';
    const lang = this.getCurrentLanguage();
    return label[lang] ?? label['default_value'] ?? '';
  }

  private resolveInitialLanguage(supported: SiteLanguage[], defaultLanguage: SiteLanguage): void {
    const supportedCodes = supported.map((l) => l.isoCode);
    const persisted = this.readPersistedLanguage();
    const detected = this.detectBrowserLangCode();

    let target: string;
    if (persisted && supportedCodes.includes(persisted)) {
      target = persisted;
    } else if (detected && supportedCodes.includes(detected)) {
      target = detected;
    } else {
      target = defaultLanguage.isoCode;
    }

    this.switchLanguage(target);
  }

  private onLanguageApplied(iso: string): void {
    // La dirección (dir) NO se toca aquí: la maneja el stream reactivo `direction$` del
    // constructor (idioma × settings). Aquí solo se persiste la preferencia.
    this.persistLanguage(iso);
  }

  /** Código de 2 letras del idioma del navegador, sin validar ni aplicar fallback. */
  private detectBrowserLangCode(): string | undefined {
    const browserLang = this.translate.getBrowserLang();
    return browserLang ? browserLang.toLowerCase().substring(0, 2) : undefined;
  }

  private readPersistedLanguage(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    try {
      return localStorage.getItem(LanguageService.STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private persistLanguage(iso: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      localStorage.setItem(LanguageService.STORAGE_KEY, iso);
    } catch {
      // Safari modo privado / cuota agotada: la persistencia es "nice to have", no crítica.
    }
  }
}
